import type { Media, File, Video } from '@/types';

interface MediaData {
  url: string | undefined;
  alt: string | undefined;
  mimeType?: string | null;
  width?: number | undefined;
  height?: number | undefined;
}

/**
 * Processes and formats media URLs from PayloadCMS
 * @param media Media object or ID from PayloadCMS
 * @param options Additional options (such as thumbnail usage)
 * @returns Formatted and validated media information
 */
export const getMediaData = (
  media: number | Media | null | undefined,
  options: {
    useThumbnail?: boolean;
  } = {}
): MediaData => {
  // Early return if media is null, undefined or number
  if (!media || typeof media === 'number') {
    return {
      url: undefined,
      alt: undefined,
    };
  }

  // Type guard: check if media object has required properties
  if (!('url' in media)) {
    return {
      url: undefined,
      alt: undefined,
    };
  }

  let mediaUrl = options.useThumbnail ? media.thumbnailURL : media.url;

  // Early return if URL is missing
  if (!mediaUrl) {
    return {
      url: undefined,
      alt: media.alt || undefined,
    };
  }

  // Add NEXT_PUBLIC_PAYLOAD_API_URL if URL doesn't start with http or https
  if (!mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
    // mediaUrl = `${process.env.NEXT_PUBLIC_PAYLOAD_API_URL || ''}${mediaUrl}`;
  }


  return {
    url: mediaUrl,
    alt: media.alt || undefined,
    // Additional properties for future use
    mimeType: media.mimeType,
    // Fallback to reasonable defaults if width/height missing
    width: media.width || undefined,
    height: media.height || undefined,
  };
};

/**
 * The first of these images the CMS actually returned. An image the site may not read comes back
 * as a bare id, not an object; Relic's card and cover images did in October 2026, and its card
 * said NO IMAGE while its share image loaded fine.
 */
export const firstMediaData = (...candidates: (number | Media | null | undefined)[]): MediaData => {
  for (const media of candidates) {
    const data = getMediaData(media);
    if (data.url) return data;
  }
  return getMediaData(undefined);
};

/**
 * A film's address, asking for its first instant. Safari draws nothing for a film it has only
 * read the header of, and leaves an empty tile with a play button; asked for a moment in the
 * film, it draws that frame. Other browsers show the first frame either way.
 */
export const firstFrame = (url: string): string => (url.includes('#') ? url : `${url}#t=0.001`);

/** The widths the image optimiser makes (Next's defaults); it refuses any other. */
const IMAGE_WIDTHS = [640, 750, 828, 1080, 1200, 1920, 2048, 3840];

/** The site's own PNG, JPEG, WebP and AVIF files. SVGs, GIFs and other hosts are left as they are. */
export const resizable = (url: string): boolean => /^\/(?!\/|_next\/)[^?#]*\.(png|jpe?g|webp|avif)$/i.test(url);

/**
 * A copy of a site image at least `width` pixels wide, made by the image optimiser. The CMS keeps
 * originals: a card on the home page loaded a 3000 x 4000 PNG of 20 MB to show it 443 px wide.
 */
export const resizedImage = (url: string, width: number): string => {
  if (!resizable(url)) return url;
  const w = IMAGE_WIDTHS.find((size) => size >= width) ?? IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1];
  return `/_next/image?url=${encodeURIComponent(url)}&w=${w}&q=75`;
};

/** The same image as a srcset of every optimiser width, or nothing when it cannot be resized. */
export const resizedSrcSet = (url: string): string | undefined =>
  resizable(url) ? IMAGE_WIDTHS.map((w) => `${resizedImage(url, w)} ${w}w`).join(', ') : undefined;

/**
 * `<img>` attributes for a site image shown at about `width` pixels: a resized copy, the srcset,
 * and lazy loading. An eager `<img>` is also preloaded by React ahead of the page's styles and
 * fonts: Beyond the Image preloaded 11 original photographs, 13.5 MB.
 */
export const resizedImageProps = (url: string | null | undefined, width: number, sizes: string) =>
  url
    ? { src: resizedImage(url, width), srcSet: resizedSrcSet(url), sizes, loading: 'lazy' as const, decoding: 'async' as const }
    : { src: undefined };

/** A CMS upload that may be animated. Next's image optimiser sends GIF and WebP animations whole. */
const STILL_SOURCE = /^\/api\/(artworkimage|media)\/file\/[^/?#]+\.(gif|webp)$/i;

/** Whether a site image may be animated, so a grid shows its first frame instead (see app/api/still). */
export const mayBeAnimated = (url: string | null | undefined): boolean => !!url && STILL_SOURCE.test(url);

/** next/image loader for the still first frame of a possibly animated upload. */
export const stillLoader = ({ src, width }: { src: string; width: number }): string =>
  `/api/still?src=${encodeURIComponent(src)}&w=${width}`;

export interface AudioData {
  url: string | null;
  duration?: number | null;
  mimeType?: string | null;
  filesize?: number | null;
  filename?: string | null;
}

export const getAudioData = (media: number | Media | File | null | undefined): AudioData => {
  if (!media || typeof media === 'number') {
    return {
      url: null,
    };
  }

  let mediaUrl = media.url || '';
  // Add NEXT_PUBLIC_PAYLOAD_API_URL if URL doesn't start with http or https
  if (!mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
    mediaUrl = `${process.env.NEXT_PUBLIC_PAYLOAD_API_URL || ''}${mediaUrl}`;
  }

  // File type check
  if ('filename' in media) {
    return {
      url: mediaUrl || null,
      mimeType: media.mimeType || null,
      filesize: media.filesize || null,
      filename: media.filename || null,
    };
  }

  // Media type check
  return {
    url: mediaUrl || null,
    mimeType: media.mimeType || null,
    filesize: media.filesize || null,
    filename: media.filename || null,
  };
};

export interface VideoOrFileData {
  url: string | null;
  mimeType: string | null;
  filesize: number | null;
  filename: string | null;
}

export const getVideoOrFileData = (media: number | Media | File | null | undefined): VideoOrFileData => {
  if (!media || typeof media === 'number') {
    return {
      url: null,
      mimeType: null,
      filesize: null,
      filename: null,
    };
  }

  let mediaUrl = media.url || '';
  // Add NEXT_PUBLIC_PAYLOAD_API_URL if URL doesn't start with http or https
  if (!mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
    mediaUrl = `${process.env.NEXT_PUBLIC_PAYLOAD_API_URL || ''}${mediaUrl}`;
  }

  // File type check
  if ('filename' in media) {
    return {
      url: mediaUrl || null,
      mimeType: media.mimeType || null,
      filesize: media.filesize || null,
      filename: media.filename || null,
    };
  }

  // Media type check
  return {
    url: mediaUrl || null,
    mimeType: media.mimeType || null,
    filesize: media.filesize || null,
    filename: media.filename || null,
  };
};

/**
 * A film for a page block: its 1080p encode, or the largest smaller one, where the CMS made
 * them; otherwise the upload, as getVideoOrFileData gives it. Blocks played the upload itself,
 * straight from the CMS: the Paul & Shark film is a 119.6 MB 4K file with a 25 MB 1080p encode.
 */
export const getFilmData = (media: number | Media | File | Video | null | undefined): VideoOrFileData => {
  const file = getVideoOrFileData(media as number | Media | File | null | undefined);
  const encoded = media && typeof media === 'object' && 'stream' in media
    ? getVideoData(media as Video)?.encoded_urls
    : null;
  const url = ['1080', '720', '480', '360'].map((size) => encoded?.[size]?.url).find(Boolean);
  return url ? { ...file, url, mimeType: 'video/mp4' } : file;
};

export interface VideoData {
  original_url: string | null;
  encoded_urls?: { [k: string]: { url: string } } | null;
  poster?: string | null;
  preview_url?: string | null;
  stream?: string | number | boolean | { [k: string]: unknown } | unknown[] | null;
  mimeType: string | null;
  filesize: number | null;
  filename: string | null;
  width?: number | null;
  height?: number | null;
  aspectRatio?: number | null;
}

interface ReturnStreamData {
  original_url: string | null;
  poster: string | null;
  preview_url: string | null;
  mimeType: string | null;
  filesize: number | null;
  filename: string | null;
  encoded_urls: { [key: string]: { url: string } };
  aspectRatio: number | null;
  width: number | null;
  height: number | null;
}

export const getVideoData = (video: number | Video | null | undefined): VideoData | null => {
  if (!video || typeof video === 'number') {
    return null;
  }

  if (video.stream && typeof video.stream === 'object' && 'ready' in video.stream && video.stream.ready === true) {
    const streamData = video.stream as {
      original_media_url: string;
      poster_url: string;
      preview_url: string;
      encodings: { [key: string]: { url: string } };
    };

    const returnStream: ReturnStreamData = {
      original_url: streamData.original_media_url || null,
      poster: streamData.poster_url || null,
      preview_url: streamData.preview_url || null,
      mimeType: video.mimeType || null,
      filesize: video.filesize || null,
      filename: video.filename || null,
      encoded_urls: {},
      aspectRatio: null,
      width: null,
      height: null,
    };

    if (streamData.encodings['360']) {
      returnStream.encoded_urls['360'] = { url: streamData.encodings['360'].url };
    }
    if (streamData.encodings['480']) {
      returnStream.encoded_urls['480'] = { url: streamData.encodings['480'].url };
    }
    if (streamData.encodings['720']) {
      returnStream.encoded_urls['720'] = { url: streamData.encodings['720'].url };
    }
    if (streamData.encodings['1080']) {
      returnStream.encoded_urls['1080'] = { url: streamData.encodings['1080'].url };
    }

    // Note: To get width, height and aspect ratio from poster URL,
    // this should be called on the client side using getImageDimensions()

    return returnStream;
  }

  let mediaUrl = video.url || '';

  // Media type check
  return {
    original_url: mediaUrl || null,
    mimeType: video.mimeType || null,
    filesize: video.filesize || null,
    filename: video.filename || null,
  };
};

/** Encodes from largest to smallest. */
const FILM_LADDER = ['1080', '720', '480', '360'];
/** The largest encode a screen gets, widest screens first. */
const FILM_STEPS: Array<{ media?: string; from: string }> = [
  { media: '(min-width: 1024px)', from: '1080' },
  { media: '(min-width: 768px)', from: '720' },
  { media: '(min-width: 480px)', from: '480' },
  { from: '360' },
];

export interface FilmSource {
  src: string;
  type: string;
  media?: string;
}

/**
 * A film's `<source>` list, widest screens first. Each screen width gets the largest encode up to
 * its step, so a film without a 1080p encode plays 720p on a laptop rather than dropping to 360p.
 * The original upload was sent to every screen 1440 px wide or more: 95 MB for one Echoes of
 * Alhambra film. Now it plays only in fullscreen on screens wider than 1920 px, or when the film
 * has no encodes at all.
 */
export function filmSources(data: VideoData | null | undefined, fullscreen = false): FilmSource[] {
  if (!data) return [];
  const original = data.original_url
    ? { src: data.original_url, type: data.mimeType || 'video/mp4' }
    : null;
  const encoded = data.encoded_urls ?? {};
  const steps = FILM_STEPS.flatMap(({ media, from }) => {
    const src = FILM_LADDER.slice(FILM_LADDER.indexOf(from)).map((k) => encoded[k]?.url).find(Boolean);
    return src ? [{ src, type: 'video/mp4', media }] : [];
  });
  if (steps.length === 0) return original ? [original] : [];

  // One encode serving several steps keeps the narrowest step's rule, which also covers the wider.
  const sources = steps.filter((step, i) => steps[i + 1]?.src !== step.src);
  // The last source has no rule, so every screen gets something.
  sources[sources.length - 1] = { ...sources[sources.length - 1], media: undefined };
  return fullscreen && original ? [{ ...original, media: '(min-width: 1921px)' }, ...sources] : sources;
}

/** Best URL to autoplay as a card background. Relative `/media` paths are rewritten by Next. */
export function playbackUrl(video: number | Video | null | undefined): string | null {
  const data = getVideoData(video);
  if (!data) return null;
  return (
    data.encoded_urls?.['720']?.url
    || data.encoded_urls?.['480']?.url
    || data.encoded_urls?.['360']?.url
    || data.preview_url
    || data.original_url
    || null
  );
}

/**
 * The 480p film for a dimmed background, such as the footer's showreel at 30% opacity, where it
 * cannot be told from 720p. For the 108-second showreel that is 21 MB instead of 41 MB.
 */
export function backgroundPlaybackUrl(video: number | Video | null | undefined): string | null {
  return getVideoData(video)?.encoded_urls?.['480']?.url || playbackUrl(video);
}

/* 
{
  "size": "59.6MB",
  "ready": true,
  "token": "iAl9AKwSZ",
  "api_url": "https://media.orkhan.art/api/v1/media/iAl9AKwSZ",
  "duration": 86,
  "encodings": {
    "360": {
      "url": "/media/encoded/3/admin/964b71f5b5d9460fb18ae58d8bc78cd9.964b71f5b5d9460fb18ae58d8bc78cd9.showreel.mp4.mp4",
      "size": "9.1MB",
      "title": "h264-360",
      "status": "success",
      "progress": 100,
      "encoding_id": 64
    },
    "480": {
      "url": "/media/encoded/13/admin/964b71f5b5d9460fb18ae58d8bc78cd9.964b71f5b5d9460fb18ae58d8bc78cd9.showreel.mp4.mp4",
      "size": "16.1MB",
      "title": "h264-480",
      "status": "success",
      "progress": 100,
      "encoding_id": 65
    },
    "720": {
      "url": "/media/encoded/10/admin/964b71f5b5d9460fb18ae58d8bc78cd9.964b71f5b5d9460fb18ae58d8bc78cd9.showreel.mp4.mp4",
      "size": "34.7MB",
      "title": "h264-720",
      "status": "success",
      "progress": 100,
      "encoding_id": 66
    },
    "1080": {
      "url": "/media/encoded/7/admin/964b71f5b5d9460fb18ae58d8bc78cd9.964b71f5b5d9460fb18ae58d8bc78cd9.showreel.mp4.mp4",
      "size": "61.2MB",
      "title": "h264-1080",
      "status": "success",
      "progress": 100,
      "encoding_id": 67
    }
  },
  "poster_url": "/media/original/thumbnails/user/admin/964b71f5b5d9460fb18ae58d8bc78cd9_ekWB2Xd.showreel.mp4.jpg",
  "preview_url": "/media/encoded/1/admin/964b71f5b5d9460fb18ae58d8bc78cd9.tmp8j58u183.gif",
  "video_height": 1080,
  "thumbnail_url": "/media/original/thumbnails/user/admin/964b71f5b5d9460fb18ae58d8bc78cd9_8UmPq9T.showreel.mp4.jpg",
  "thumbnail_time": 8.3,
  "original_media_url": "/media/original/user/admin/964b71f5b5d9460fb18ae58d8bc78cd9.showreel.mp4"
}
*/
